// src/components/Operations/Fleet/FleetDashboard.jsx
import React, { useEffect, useMemo, useState } from 'react';
import useFleetApi from './useFleetApi';
import { daysUntil, groupBy } from './reportUtils';
import AnalyticsMetricCard from '../../Analytics/AnalyticsMetricCard';
import WidgetGrid from '../../ui/WidgetGrid';
import { num } from '../../Analytics/metricValue';
import '../OpsShared.css';
import './FleetReports.css';

// A count is a real number (0 is a real 0); unknown input renders "—".
const count = (v) => {
  const n = num(v);
  return n === null ? '—' : n.toLocaleString();
};

export default function FleetDashboard(){
  const { listVehicles } = useFleetApi();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try{
        const data = await listVehicles();
        setRows(Array.isArray(data) ? data : (data.results || []));
      } finally{ setLoading(false); }
    })();
  }, [listVehicles]);

  const kpis = useMemo(() => {
    const total = rows.length;
    const active = rows.filter(v => v.status === 'active').length;
    const inSvc  = rows.filter(v => v.status === 'in_service').length;

    const overdueMaint = rows.filter(v => {
      const milesPast = (v.odometer ?? 0) - (v.next_service_due_miles ?? Infinity);
      const datePast  = daysUntil(v.next_service_due_date) ?? Infinity;
      return milesPast >= 0 || datePast < 0;
    }).length;

    const expiringInsurance = rows.filter(v => {
      const d = daysUntil(v?.insurance?.expires_on);
      return d != null && d <= 30;
    }).length;

    return [
      { key:'total',   title:'Total units',          subtitle:'Vehicles in the fleet',      value: count(total) },
      { key:'active',  title:'Active',               subtitle:'In regular service',          value: count(active),  tone:'good' },
      { key:'insvc',   title:'In service',           subtitle:'Shop / maintenance',          value: count(inSvc) },
      { key:'overdue', title:'Overdue maintenance',  subtitle:'Past due by miles or date',   value: count(overdueMaint), tone: overdueMaint ? 'bad' : 'neutral' },
      { key:'expins',  title:'Insurance ≤ 30 days',  subtitle:'Expiring within a month',     value: count(expiringInsurance), tone: expiringInsurance ? 'bad' : 'neutral' },
    ];
  }, [rows]);

  const byType = useMemo(() => {
    const g = groupBy(rows, v => v.type || 'other');
    return Object.entries(g).map(([type, list]) => ({ type, count: list.length }));
  }, [rows]);

  return (
    <div className="ops-page">
      <header className="ops-head">
        <div>
          <h1>Fleet overview</h1>
          <div className="ops-muted">Units, status, and what needs attention — maintenance due and insurance expiring soon.</div>
        </div>
      </header>

      <WidgetGrid columns={5}>
        {kpis.map(k => (
          <AnalyticsMetricCard
            key={k.key}
            title={k.title}
            subtitle={k.subtitle}
            value={k.value}
            periodLabel="current"
            loading={loading}
            tone={k.tone || 'neutral'}
          />
        ))}
      </WidgetGrid>

      <section className="ops-section">
        <div className="ops-section__head">
          <h3>Composition by type</h3>
        </div>
        <div className="bars">
          {byType.length === 0 && <p className="ops-muted">No vehicles.</p>}
          {byType.map(r => (
            <div key={r.type} className="bar-row">
              <span className="bar-label cap">{r.type}</span>
              <div className="bar-track">
                <div className="bar-fill" style={{ width: `${(r.count / Math.max(1, rows.length))*100}%` }} />
              </div>
              <span className="bar-value">{count(r.count)}</span>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
